import { ArrowUp } from 'lucide-react'
import {
  addTransitionType,
  startTransition,
  useEffect,
  useLayoutEffect,
  useState,
  ViewTransition,
} from 'react'
import { useLocation } from '#router'

import { CheckpointProvider } from '@/components/checkpoint-context'
import { useIsMobile } from '@/hooks/use-mobile'
import CourseContent from './course-content'
import PreviousNext from './previous-next'
import Sidebar from './sidebar'
import StickyNav from './sticky-nav'
import { Button } from './ui/button'

export default function CourseDetail() {
  const isMobile = useIsMobile()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [hydrated, setHydrated] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const _location = useLocation()

  // Mark as hydrated on mount
  useLayoutEffect(() => {
    setHydrated(true)
  }, [])

  // Track scroll position for back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const closeSidebar = () => {
    startTransition(() => {
      addTransitionType('sidebar-close')
      setSidebarOpen(false)
    })
  }

  const toggleSidebar = () =>
    startTransition(() => {
      addTransitionType(sidebarOpen ? 'sidebar-close' : 'sidebar-open')
      setSidebarOpen((prev) => !prev)
    })

  useLayoutEffect(() => {
    if (!isMobile) return
    setSidebarOpen(false)
  }, [isMobile])

  return (
    <div
      className="course-detail relative flex"
      data-sidebar-open={sidebarOpen}
      data-hydrated={hydrated}
    >
      {/* Mobile backdrop */}

      <ViewTransition
        enter={{
          default: 'none',
          'sidebar-open': 'fade-in duration-300',
        }}
        exit={{
          default: 'none',
          'sidebar-close': 'fade-out duration-300',
        }}
      >
        <div
          className="mobile-backdrop absolute inset-0 z-10 bg-black/50"
          onClick={closeSidebar}
        />
      </ViewTransition>

      <ViewTransition
        update={{
          default: 'none',
          'sidebar-open': 'sidebar-enter',
          'sidebar-close': 'sidebar-exit',
        }}
      >
        <Sidebar
          className="flex-none"
          open={sidebarOpen}
          onClose={closeSidebar}
        />
      </ViewTransition>

      <div className="@container min-w-0 flex-auto">
        <ViewTransition
          update={{
            default: 'none',
            'sidebar-open': 'course-content-open',
            'sidebar-close': 'course-content-close',
          }}
        >
          <StickyNav
            sidebarOpen={sidebarOpen}
            onSidebarToggle={toggleSidebar}
          />
        </ViewTransition>

        <ViewTransition
          update={{
            default: 'none',
            'sidebar-open': 'course-content-open',
            'sidebar-close': 'course-content-close',
          }}
        >
          <div className="mx-auto w-full max-w-screen-md px-6 py-10">
            <CheckpointProvider>
              <CourseContent />
            </CheckpointProvider>
            <PreviousNext className="mt-8 border-t pt-8" />
          </div>
        </ViewTransition>
      </div>

      {/* Back to top button */}
      <Button
        variant="outline"
        size="icon"
        className={`fixed right-6 bottom-6 z-30 rounded-full shadow-sm transition-all duration-300 ${
          showBackToTop
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-4 opacity-0'
        }`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ArrowUp className="size-5" />
      </Button>
    </div>
  )
}
